import type { FontDictionary, FontFamily } from '../types/fonts';
import { fetchGoogleFonts } from './data';
import { getFontFormat } from './utils';

export class FontManager {
  private dictionary: FontDictionary;

  constructor(dictionary: FontDictionary = {}) {
    this.dictionary = dictionary;
  }

  static async createWithFonts(apiKey: string = ''): Promise<FontManager> {
    try {
      const fonts = await fetchGoogleFonts(apiKey);
      return new FontManager(fonts);
    } catch (error) {
      console.error('Failed to create FontManager with fonts:', error);
      return new FontManager();
    }
  }

  /**
   * Get font URL by family name, weight, and style properties
   * This is more reliable than variant string matching
   */
  getFontUrlByProps(fontFamily: string, weight: string, style: 'normal' | 'italic'): string | null {
    const font = this.dictionary[fontFamily];
    if (!font) return null;

    const match = font.variants.find((v) => v.weight === weight && v.style === style);
    return match ? match.url : null;
  }

  /**
   * Get all available font families
   */
  getAllFontFamilies(): string[] {
    return Object.keys(this.dictionary);
  }

  getFontData(fontFamily: string): FontFamily | undefined {
    return this.dictionary[fontFamily];
  }

  /**
   * Get available variants for a font family
   */
  getAvailableVariants(fontFamily: string): string[] {
    const font = this.dictionary[fontFamily];
    if (!font) return [];

    const uniqueVariants = font.variants.reduce((acc, variant) => {
      if (!acc.includes(variant.variant)) {
        acc.push(variant.variant);
      }
      return acc;
    }, [] as string[]);

    return uniqueVariants;
  }

  getAvailableWeights(
    fontFamily: string,
  ): Array<{ label: string; value: string; weight: string; style: string }> {
    const font = this.dictionary[fontFamily];
    if (!font) return [];

    const weightMap: Record<string, string> = {
      '100': 'Thin',
      '200': 'Extra Light',
      '300': 'Light',
      '400': 'Regular',
      '500': 'Medium',
      '600': 'Semi Bold',
      '700': 'Bold',
      '800': 'Extra Bold',
      '900': 'Black',
    };

    const weights = font.variants.map((variant) => ({
      label: `${weightMap[variant.weight] || variant.weight} ${variant.weight}`,
      value: variant.variant,
      weight: variant.weight,
      style: variant.style,
    }));

    const uniqueWeights = weights
      .filter((weight, index, self) => index === self.findIndex((w) => w.value === weight.value))
      .sort((a, b) => parseInt(a.weight) - parseInt(b.weight));

    return uniqueWeights;
  }

  /**
   * Generate @font-face CSS for a font
   * This method handles all the font CSS generation logic
   */
  getFontFaceCss(fontFamily?: string, fontWeight?: string, fontStyle?: string): string {
    if (!fontFamily || !fontWeight) {
      return '';
    }

    const normalizedStyle: 'normal' | 'italic' = fontStyle === 'italic' ? 'italic' : 'normal';
    const fontUrl = this.getFontUrlByProps(fontFamily, fontWeight, normalizedStyle);

    if (!fontUrl) {
      return '';
    }

    const format = getFontFormat(fontUrl);
    return `@font-face {
    font-family: '${fontFamily}';
    src: url('${fontUrl}')${format ? ` format('${format}')` : ''};
    font-style: ${normalizedStyle};
    font-weight: ${fontWeight};
    font-display: swap;
  }`;
  }
}

// Module singleton for FontManager
let fontManagerInstance: FontManager | null = null;

/**
 * Get the global FontManager instance
 */
export const getFontManager = (): FontManager | null => {
  return fontManagerInstance;
};

/**
 * Set the global FontManager instance
 */
export const setFontManager = (manager: FontManager): void => {
  fontManagerInstance = manager;
};

/**
 * Clear the global FontManager instance
 */
export const clearFontManager = (): void => {
  fontManagerInstance = null;
};
